import React, { useState, FunctionComponent, ReactElement } from "react";
import { useTheme } from "@mui/material";
import WDShortcuts from "./WDShortcuts";

import { IOrderDataHistorical } from "../../models/Interfaces";
import WDGameFinishedOverlay from "./WDGameFinishedOverlay";
import { Unit } from "../../utils/map/getUnits";
import {
  TopLeft,
  TopRight,
  BottomLeft,
  BottomRight,
  BottomMiddle,
} from "./main-screen";
import { useAppSelector } from "../../state/hooks";
import {
  gameOverview,
  gameStatus,
  gameViewedPhase,
} from "../../state/game/game-api-slice";
import { CountryTableData } from "../../interfaces";
import { getActiveMapVariant } from "../../map/variants";
import Country, { abbrMap } from "../../enums/Country";
import {
  getGamePhaseSeasonYear,
  getHistoricalPhaseSeasonYear,
} from "../../utils/state/getPhaseSeasonYear";
import WDClassesJIT from "./WDClassesJIT";
import WDSpectatorBanner from "./WDSpectatorBanner";
// import WDLoading from "../miscellaneous/Loading";

interface WDUIProps {
  orders: IOrderDataHistorical[];
  units: Unit[];
  viewingGameFinishedPhase: boolean;
}

const WDUI: FunctionComponent<WDUIProps> = function ({
  orders,
  units,
  viewingGameFinishedPhase,
}): ReactElement {
  const theme = useTheme();
  const { countryMap, countryIDMap } = getActiveMapVariant();
  const { phase, season, year, user, members, gameID } =
    useAppSelector(gameOverview);

  const [phaseSelectorOpen, setPhaseSelectorOpen] = useState<boolean>(false);
  const gameIsFinished = phase === "Finished";

  const allCountries: CountryTableData[] = [];

  const getCountrySortIdx = function (countryID: number) {
    // Sort user country to the front
    if (countryID === user?.member.countryID) return -1;
    return countryID;
  };

  const constructTableData = (member) => {
    /*
     * 后端给的是国家名（如 "Peru"），地图变体的 countryMap 负责翻译成枚举。
     * 万一变体还没切过来（overview 刚到、game/data 未回）或者某张图漏配了
     * 国家，这里退回按 countryID 查、再退回原始名字 —— 之前直接取
     * countryMap[member.country] 得到 undefined，国家表对它调 toUpperCase()
     * 就会抛异常，整页白屏。
     */
    const memberCountry: Country =
      countryMap[member.country] ??
      countryIDMap[member.countryID] ??
      (member.country || ("" as Country));
    return {
      ...member,
      abbr: abbrMap[member.country] ?? String(member.country || "").slice(0, 3).toUpperCase(),
      color: theme.palette[memberCountry]?.main,
      power: memberCountry,
      votes: member.votes,
    };
  };

  members.forEach((member) => {
    allCountries.push(constructTableData(member));
  });
  allCountries.sort(
    (x, y) => getCountrySortIdx(x.countryID) - getCountrySortIdx(y.countryID),
  );

  const userTableData = user ? constructTableData(user.member) : null;

  const gameStatusData = useAppSelector(gameStatus);

  const { viewedPhaseIdx } = useAppSelector(gameViewedPhase);

  const {
    phase: gamePhase,
    season: gameSeason,
    year: gameYear,
  } = getGamePhaseSeasonYear(phase, season, year);

  let {
    phase: viewedPhase,
    season: viewedSeason,
    year: viewedYear,
  } = getHistoricalPhaseSeasonYear(gameStatusData, viewedPhaseIdx);

  // On the very last phase of a finished game, webdip API might give an
  // entirely erroneous year/season/phase. So instead, trust the one in the
  // overview.
  if (
    viewedPhaseIdx === gameStatusData.phases.length - 1 &&
    gamePhase === "Finished"
  ) {
    viewedPhase = gamePhase;
    viewedSeason = gameSeason;
    viewedYear = gameYear;
  }

  return (
    <>
      <TopLeft
        gamePhase={gamePhase}
        gameSeason={gameSeason}
        gameYear={gameYear}
        viewedPhase={viewedPhase}
        viewedSeason={viewedSeason}
        viewedYear={viewedYear}
        orders={orders}
        phaseSelectorOpen={phaseSelectorOpen}
      />
      <TopRight />
      <WDSpectatorBanner />
      {!gameIsFinished && <BottomLeft phaseSelectorOpen={phaseSelectorOpen} />}
      <BottomMiddle
        viewedSeason={viewedSeason}
        viewedYear={viewedYear}
        viewedPhase={viewedPhase}
        totalPhases={gameStatusData.phases.length}
      />
      <WDClassesJIT />
      <WDShortcuts
        onPhaseSelectorShortcut={() => setPhaseSelectorOpen(!phaseSelectorOpen)}
      />
      {/* <WDLoading
        show={showLoading}
        onLoadingFinished={() => setShowLoading(false)}
      >
        Next phase
      </WDLoading> */}
      {gameIsFinished && viewingGameFinishedPhase && (
        <WDGameFinishedOverlay allCountries={allCountries} />
      )}
      {/* A visitor is not playing a country, so the order panel — which is
          how orders are entered — has nothing to act on. Phase browsing stays
          available so they can still look back through the game. */}
      {user && (
        <BottomRight
          phaseSelectorOpen={phaseSelectorOpen}
          onPhaseSelectorClick={() => setPhaseSelectorOpen(!phaseSelectorOpen)}
          orders={orders}
          units={units}
          allCountries={allCountries}
          userTableData={userTableData}
          viewedPhase={viewedPhase}
          currentSeason={viewedSeason}
          currentYear={viewedYear}
          totalPhases={gameStatusData.phases.length}
          onClickOutside={() => setPhaseSelectorOpen(false)}
        />
      )}
    </>
  );
};

export default WDUI;
